import React, { useMemo, useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import type { CatalogEntry } from './v2-settings-api';
import {
  itemRegexHint,
  itemRegexLabel,
  normalizeStringArrayItem,
  parseStringArrayText,
  splitListInput,
  stringArrayToText,
  validateStringArrayItems,
} from './customer-override-editor.helpers';

interface Props {
  name: string;
  def: CatalogEntry;
  // The row draft's JSON text (RowDraft.valueText) — the single source of truth
  // shared with dirty-checks and the PATCH body.
  valueText: string;
  disabled?: boolean;
  onChange: (valueText: string) => void;
}

/**
 * Chip-list editor for stringArray settings (ticket 868m81zbq).
 *
 * Values are typed one at a time — Enter, comma, or leaving the field commits
 * — or pasted as a comma/newline-separated list. Each item is normalized for
 * the row's itemRegex (US phone numbers become +1XXXXXXXXXX) and invalid ones
 * are highlighted with the reason, so the problem is visible before Save all
 * instead of after a silent server rejection.
 */
export const StringArrayField: React.FC<Props> = ({ name, def, valueText, disabled, onChange }) => {
  const items = useMemo(() => parseStringArrayText(valueText), [valueText]);
  const [input, setInput] = useState('');
  const itemRe = useMemo(() => (def.itemRegex ? new RegExp(def.itemRegex) : null), [def.itemRegex]);
  const problem = validateStringArrayItems(items, def);
  const label = itemRegexLabel(def.itemRegex);

  const commit = (next: string[]) => {
    const normalized = next
      .flatMap((v) => splitListInput(String(v)))
      .map((v) => normalizeStringArrayItem(v, def.itemRegex))
      .filter(Boolean);
    onChange(stringArrayToText(Array.from(new Set(normalized))));
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      disableClearable
      options={[] as string[]}
      value={items}
      inputValue={input}
      disabled={disabled}
      size="small"
      onInputChange={(_, next, reason) => {
        if (reason === 'reset') {
          setInput('');
          return;
        }
        // A comma (or pasted list) commits what's typed so far.
        if (/[,;\n]/.test(next)) {
          const parts = splitListInput(next);
          if (parts.length > 0) commit([...items, ...parts]);
          setInput('');
          return;
        }
        setInput(next);
      }}
      onChange={(_, next) => {
        commit(next as string[]);
        setInput('');
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const invalid = itemRe ? !itemRe.test(option) : false;
          const { key, ...tagProps } = getTagProps({ index }) as any;
          return (
            <Chip
              key={key ?? `${option}-${index}`}
              {...tagProps}
              label={option}
              size="small"
              color={invalid ? 'error' : 'default'}
              variant={invalid ? 'outlined' : 'filled'}
              title={invalid ? `Not a valid ${itemRegexHint(def.itemRegex)}` : undefined}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={items.length === 0 ? `Add ${label}` : ''}
          error={!!problem}
          helperText={problem ?? `Press Enter or comma after each ${label}.`}
          inputProps={{ ...params.inputProps, 'aria-label': `${name} values` }}
        />
      )}
    />
  );
};
